"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function useScrollState(threshold = 20) {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [threshold]);
  return isScrolled;
}
function useSmoothScroll() {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[10px] uppercase tracking-[0.1em] font-medium text-foreground/80 hover:text-foreground transition-colors block py-2">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-8 animate-pulse">
        <div className="h-6 w-32 bg-muted rounded" />
        <div className="h-6 w-24 bg-muted rounded" />
        <div className="h-6 w-28 bg-muted rounded" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-8">
      {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium tracking-wide text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="pt-4 border-t border-border mt-4">
        <Link to="/WaitlistPage" className="text-lg font-medium tracking-wide text-foreground hover:text-primary transition-colors block" onClick={onClose}>
          Join Waitlist
        </Link>
      </div>
    </nav>;
}
export default function Header() {
  const isScrolled = useScrollState(20);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useSmoothScroll();
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b ${isScrolled ? "bg-background/80 backdrop-blur-lg border-border/40 shadow-sm" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-1 flex justify-start">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1558603/img/paawan_logo.png" alt="PāAWAN" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="17" className="header-menu-desktop">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex-1 flex justify-end items-center gap-4">
          <Button variant="ghost" className="hidden md:inline-flex text-[10px] uppercase tracking-[0.1em] font-medium hover:bg-transparent hover:text-foreground/70 px-0" asChild>
            <Link to="/WaitlistPage">Join Waitlist</Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {}
      <div className={`fixed inset-0 z-50 bg-background md:hidden transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 h-20 border-b border-border">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1558603/img/paawan_logo.png" alt="PāAWAN" className="h-7 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="17" className="header-menu-mobile">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>
      </div>
    </header>;
}